import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import qrConfigService from '../services/qrConfigService';

const DEFAULT_CONFIG = {
  qrOrderingEnabled: true,
  requireStaffApproval: false,
  autoActivateSession: true,
  sessionTimeoutHours: 2,
  maxOrdersPerSession: 10,
  baseUrl: '',
};

export default function useQRConfig() {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isNew, setIsNew] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await qrConfigService.getConfig();
      if (data && data.id) {
        setConfig(data);
        setIsNew(false);
      } else {
        setConfig(DEFAULT_CONFIG);
        setIsNew(true);
      }
    } catch (err) {
      if (err?.response?.status === 404) {
        setConfig(DEFAULT_CONFIG);
        setIsNew(true);
      } else {
        setError(err.message || 'Failed to load QR config');
        toast.error('Failed to load QR configuration');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const saveConfig = async (data) => {
    try {
      const payload = { ...config, ...data };
      let result;
      if (isNew) {
        result = await qrConfigService.createConfig(payload);
      } else {
        result = await qrConfigService.updateConfig(payload);
      }
      setConfig(result);
      setIsNew(false);
      toast.success('QR configuration saved successfully');
      return result;
    } catch (err) {
      toast.error(err.message || 'Failed to save QR configuration');
      throw err;
    }
  };

  const toggleOrdering = async () => {
    try {
      const result = await qrConfigService.toggleOrdering();
      setConfig((prev) => ({ ...prev, qrOrderingEnabled: result.qrOrderingEnabled }));
      toast.success(result.qrOrderingEnabled ? 'QR ordering enabled' : 'QR ordering disabled');
      return result;
    } catch (err) {
      toast.error('Failed to toggle QR ordering');
      throw err;
    }
  };

  const resetDefaults = () => {
    setConfig((prev) => ({
      ...prev,
      requireStaffApproval: DEFAULT_CONFIG.requireStaffApproval,
      autoActivateSession: DEFAULT_CONFIG.autoActivateSession,
      sessionTimeoutHours: DEFAULT_CONFIG.sessionTimeoutHours,
      maxOrdersPerSession: DEFAULT_CONFIG.maxOrdersPerSession,
    }));
    toast.success('Settings reset to defaults');
  };

  const deleteConfig = async () => {
    try {
      await qrConfigService.deleteConfig();
      setConfig(DEFAULT_CONFIG);
      setIsNew(true);
      toast.success('QR configuration deleted');
    } catch (err) {
      toast.error('Failed to delete QR configuration');
      throw err;
    }
  };

  return {
    config,
    loading,
    error,
    isNew,
    saveConfig,
    toggleOrdering,
    resetDefaults,
    deleteConfig,
    refreshConfig: fetchConfig,
  };
}
